import classNames from 'classnames'
import React, { useState } from 'react'
import { useTranslations } from '../hooks/useTranslations'
import { isNullOrWhiteSpace } from '../utilities/string'
import { Button } from './Button'
import { Grid } from './Grid'
import { ImageProps } from './Image'
import { PauseButton } from './PauseButton'
import { SoundButton } from './SoundButton'
import { VideoModal } from './VideoModal'
import { VimeoVideo } from './VimeoVideo'

export interface FullWidthVideoHeroProps {
  buttonLabel?: string
  playVideoCallback?: (videoUrl: string) => void
  previewImage?: ImageProps
  /**
   * Vimeo only
   */
  previewVideoUrl?: string
  subtitle?: string
  theme?: string
  title: string
  /**
   * Youtube or Vimeo
   */
  videoUrl?: string
}

export const FullWidthVideoHero = ({
  buttonLabel,
  playVideoCallback,
  previewImage,
  previewVideoUrl,
  subtitle,
  theme,
  title,
  videoUrl,
}: FullWidthVideoHeroProps) => {
  const translations = useTranslations()
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [previewPaused, setPreviewPaused] = useState(false)
  const [previewStarted, setPreviewStarted] = useState(false)
  const [previewSoundEnabled, setPreviewSoundEnabled] = useState(false)

  const playVideo = () => {
    if (!isNullOrWhiteSpace(videoUrl)) {
      setPreviewSoundEnabled(false)

      if (playVideoCallback) {
        // let parent control decide how to play the video (e.g. shared VideoModal)
        playVideoCallback(videoUrl)
      } else {
        // use local video modal
        setVideoPlaying(true)
      }
    }
  }

  const onPause = () => {
    setPreviewPaused(prev => !prev)
  }

  return (
    <>
      <div
        className={classNames(
          'w-full min-h-[37.5rem] sm:min-h-[41.25rem] md:min-h-[43.125rem] max-w-[160rem] mx-auto',
          'relative',
          'flex items-end justify-center'
        )}
      >
        <div className="overflow-hidden absolute inset-0">
          <div
            className={classNames(
              'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-full aspect-wide',
              'min-w-[65.625rem]',
              'tb:min-w-[71.875rem]',
              'sm:min-h-[45rem]',
              'md:min-w-full md:h-auto' // fixed sizes to work without aspect-wide (iOS < 15) up to md
            )}
          >
            <VimeoVideo
              imgElementClassName="h-full w-full object-cover"
              loading="eager"
              muted={!previewSoundEnabled}
              onBackgroundVideoStarted={() => setPreviewStarted(true)}
              paused={previewPaused}
              previewImage={previewImage}
              previewImageClassName="absolute inset-0"
              title=""
              videoUrl={previewVideoUrl ?? ''}
            />

            <div className="absolute inset-0 bg-neutral-contrast/15">{/* Overlay */}</div>
          </div>
        </div>

        <Grid additionalClassName="z-10">
          <div
            className={classNames(
              'col-span-full',
              'tb:col-span-3 tb:ml-60',
              'sm:col-start-2 sm:col-span-4 sm:-ml-30 sm:mr-240',
              'md:col-start-1 md:ml-0',
              'lg:col-start-2 lg:mr-60',
              'xl:mx-30',
              'xxl:col-start-4 xxl:col-span-4',
              'py-30 tb:py-45 sm:py-45 md:py-90',
              'text-neutral'
            )}
          >
            {!isNullOrWhiteSpace(theme) && (
              <small className="block mb-10 font-hero-subtitle text-hero-subtitle md:mb-30">
                {theme}
              </small>
            )}

            <h1
              className={classNames('mb-15 font-hero-title text-hero-title', 'md:mb-20')}
            >
              {title}
            </h1>

            {!isNullOrWhiteSpace(subtitle) && (
              <p className="font-hero-body text-hero-body">{subtitle}</p>
            )}

            {!playVideoCallback && !isNullOrWhiteSpace(videoUrl) && (
              <VideoModal
                onClose={() => setVideoPlaying(false)}
                open={videoPlaying}
                videoUrl={videoUrl}
              />
            )}

            {!isNullOrWhiteSpace(videoUrl) && (
              <Button
                additionalClassName="mt-20 md:mt-30"
                lightBorder
                onClick={() => playVideo()}
              >
                {isNullOrWhiteSpace(buttonLabel)
                  ? translations.learnMore ?? 'Learn more'
                  : buttonLabel}
              </Button>
            )}
          </div>
        </Grid>

        {previewStarted && (
          <div
            className={classNames(
              'absolute flex gap-15 z-10 top-30 right-30',
              'tb:top-auto tb:bottom-30',
              'lg:bottom-60 lg:right-90'
            )}
          >
            <PauseButton onClick={onPause} />

            <SoundButton
              active={previewSoundEnabled}
              onClick={() => setPreviewSoundEnabled(old => !old)}
            />
          </div>
        )}
      </div>
    </>
  )
}
